import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getJobPosts } from "../../api/axios";
import { doctorImageUrl } from "../../api/imageUrl";
import { useAuth } from "../../auth/AuthContext";
import { timeAgo } from "../../utils/timeAgo";

function Avatar({ name, image }) {
  const src = doctorImageUrl(image);
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="w-10 h-10 rounded-full object-cover shrink-0 border border-gray-100"
        onError={(e) => { e.target.style.display = "none"; }}
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded-full bg-[#00342b] text-white flex items-center justify-center font-bold shrink-0">
      {(name || "?").trim().charAt(0).toUpperCase()}
    </div>
  );
}

const BuildingIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1m4 0h1m-6 4h1m4 0h1m-6 4h1m4 0h1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PinIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function JobsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      const res = await getJobPosts({ q });
      if (active) { setJobs(res.data || []); setLoading(false); }
    })();
    return () => { active = false; };
  }, [q]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Jobs</p>
          <h1 className="text-3xl font-extrabold text-[#00342b]">Find your next opportunity</h1>
          <p className="text-sm text-gray-500 mt-1">{jobs.length} open {jobs.length === 1 ? "position" : "positions"} from the community</p>
        </div>
        <div className="flex gap-3 shrink-0">
          {user && <Link to="/jobs/create" className="px-5 py-3 rounded-xl bg-[#00342b] text-white font-semibold hover:bg-[#004d40] transition">Post a job</Link>}
        </div>
      </div>

      <div className="flex gap-3 flex-wrap mb-6 border-b pb-4">
        <Link to="/jobs" className="px-4 py-2 rounded-full border text-sm font-semibold bg-[#00342b] text-white border-[#00342b]">Posted jobs</Link>
        <Link to="/jobs/available" className="px-4 py-2 rounded-full border text-sm font-semibold bg-white text-gray-600 hover:border-gray-300 transition">Available doctors</Link>
        <input
          className="px-4 py-2 rounded-full border flex-1 min-w-[220px] focus:outline-none focus:ring-2 focus:ring-[#00342b]/20"
          placeholder="Search by title, hospital, or location"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="grid gap-4">
        {loading && (
          <div className="p-10 text-center text-gray-400 bg-white border rounded-3xl">Loading job posts…</div>
        )}

        {!loading && jobs.map((j) => (
          <div
            key={j.id}
            onClick={() => navigate(`/jobs/${j.id}`)}
            className="bg-white border rounded-3xl p-5 hover:shadow-md hover:-translate-y-0.5 transition cursor-pointer group"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-100">
                  <UsersIcon /> {j.vacancy_available} {Number(j.vacancy_available) === 1 ? "opening" : "openings"}
                </span>
              </div>
              <span className="text-xs text-gray-400 shrink-0">{timeAgo(j.created_at)}</span>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mt-3 group-hover:text-[#00342b] transition">{j.post_name}</h2>

            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="flex items-center gap-1.5"><BuildingIcon /> {j.hospital_name}</span>
              <span className="flex items-center gap-1.5"><PinIcon /> {j.job_location}</span>
            </div>

            <p className="text-gray-600 mt-3 line-clamp-2 text-sm">{j.description}</p>

            <div className="flex items-center gap-3 mt-4 pt-4 border-t">
              <Avatar name={j.author_name} image={j.author_image} />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-800">{j.author_name || "Member"}</div>
                {j.author_specialty && <div className="text-xs text-gray-500">{j.author_specialty}</div>}
              </div>
              <span className="ml-auto text-sm font-semibold text-[#00342b] opacity-0 group-hover:opacity-100 transition shrink-0">View details →</span>
            </div>
          </div>
        ))}

        {!loading && !jobs.length && (
          <div className="p-10 text-center text-gray-500 bg-white border rounded-3xl">
            No job posts yet. {user ? <Link to="/jobs/create" className="text-[#00342b] font-semibold underline">Post the first one.</Link> : "Check back soon."}
          </div>
        )}
      </div>
    </div>
  );
}
